import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

const STEP_KEYS = {
  10: 'personalization_type',
  1: 'scents',
  2: 'personalization',
  3: 'theme',
  4: 'design',
  5: 'engraving_text',
  6: 'review',
  7: 'upsell',
  8: 'upload_design',
  9: 'candle_preview',
};

const STEP_EVENTS = {
  10: 'personalized_steps_personalization_type_seen',
  1: 'personalized_steps_scents_seen',
  2: 'personalized_steps_engraving_option_seen',
  3: 'personalized_steps_theme_seen',
  4: 'personalized_steps_design_seen',
  5: 'personalized_steps_engraving_text_seen',
  6: 'personalized_steps_review_seen',
  7: 'personalized_steps_upsell_seen',
  8: 'personalized_steps_upload_design_seen',
  9: 'personalized_steps_candle_preview_seen',
};

const seenStepsThisTab = new Set();
const ENGRAVING_FONT_NAMES = ['mono', 'victor', 'cutive'];
const ENGRAVING_FONT_LABELS = {
  mono: 'Mono',
  victor: 'Victor',
  cutive: 'Cutive',
};
const ENGRAVING_EMOJI_REGEX = /[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]/u;

/**
 * Personalized Steps Popup Component - Hardcoded 6 Steps
 * 
 * Step 1: Choose Scents (Required, max 3)
 * Step 2: Select your engraving option (Required)
 * Step 3: Select Theme (Skippable)
 * Step 4: Choose Design (Skippable)
 * Step 5: Text to Engrave (Skippable)
 * Step 6: Review & Confirm
 * Step 8: Upload Custom Design (For "My Design" option)
 */
export class PersonalizedStepsPopupComponent extends Component {
  requiredRefs = [
    'triggerButton',
    'popup',
    'overlay',
    'stepContainer',
    'prevButton',
    'stepCounter',
    'nextButton',
    'closeButton',
    'zoomModal',
    'zoomOverlay',
    'zoomImage',
    'zoomTitle',
    'zoomCloseButton',
    'uploadModal',
    'uploadOverlay',
    'uploadFileInput',
    'uploadPreviewContainer',
    'uploadPreviewImage',
    'uploadPdfPlaceholder',
    'uploadPdfFilename',
    'uploadArea',
    'uploadDropzone',
    'uploadConfirmButton',
    'uploadCancelButton',
    'uploadCloseButton',
    'uploadRemoveButton',
    'uploadDesignRadio',
    'uploadPlaceholder',
    'uploadPreview',
    'candlePreviewContainer',
    // Note: 'upsellButton' is optional - only present when upsell product is configured
  ];

  // Hardcoded: 6 total steps
  currentStep = 1;
  // totalSteps = 6; // Calculated dynamically based on enabled steps
  
  // Track completed actions in steps (for auto-progression)
  completedStepActions = new Set();
  
  // Collected data
  selectedScents = [];
  selectedEngravingOption = {
    value: null,
    label: null
  };
  selectedTheme = {
    value: null,
    label: null
  };
  /**
   * @type {Array<{value: string, label: string}> | {value: string | null, label: string | null} | null}
   */
  selectedDesign = []; // Changed to array to support multiple selections
  engravingText = '';
  engravingTexts = [];
  selectedQuantity = 1;
  uploadedFile = null;
  validationErrorMessage = null;
  logoLinkId = null; // Unique ID to link product with custom design product

  // Upsell configuration
  upsellSelected = false;
  upsellVariantId = null;
  upsellProductTitle = null;
  upsellLinkId = null; // Unique ID to link main product with upsell product
  upsellQuantity = 1;

  // Custom design configuration
  customDesignVariantId = null;

  // Multi-select configuration
  enableMultipleDesigns = false;
  maxDesigns = 1;

  // Track design selection order for proper FIFO behavior
  designSelectionOrder = [];

  // Translations
  translations = {};
  popupInstanceId = null;
  hasTrackedCompletionForJourney = false;
  engravingRowCounter = 0;

  connectedCallback() {
    super.connectedCallback();
    this.loadTranslations();
    this.loadDesignConfiguration();
    this.loadUpsellConfiguration();
    this.loadStepConfiguration(); // Must be after loadUpsellConfiguration to check upsellVariantId
    this.setupEventListeners();
    this.updateUI();
  }

  loadDesignConfiguration() {
    this.enableMultipleDesigns = this.getAttribute('data-enable-multiple-designs') === 'true';
    const maxDesignsAttr = this.getAttribute('data-max-designs');
    this.maxDesigns = maxDesignsAttr ? parseInt(maxDesignsAttr) : 1;
  }

  loadUpsellConfiguration() {
    this.upsellVariantId = this.getAttribute('data-upsell-variant-id');
    this.upsellProductTitle = this.getAttribute('data-upsell-product-title');
    this.customDesignVariantId = this.getAttribute('data-custom-design-variant-id');
  }

  loadStepConfiguration() {
    this.stepConfiguration = {};
    // Steps 1 to 10
    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].forEach(step => {
      // Use getAttribute to avoid ambiguity with dataset property naming for numbered attributes
      const attrName = `data-step-${step}-enabled`;
      const value = this.getAttribute(attrName);
      // Default to true if attribute is missing, but it should be present from Liquid
      this.stepConfiguration[step] = value !== 'false';
    });

    // The upsell is handled in the cart drawer instead of this popup.
    this.stepConfiguration[7] = false;
  }

  isStepEnabled(step) {
    return this.stepConfiguration[step] === true;
  }

  loadTranslations() {
    try {
      const translationsAttr = this.dataset.translations;
      if (translationsAttr) {
        this.translations = JSON.parse(translationsAttr);
      }
    } catch (error) {
      console.error('Failed to parse translations:', error);
      // Fallback to English defaults
      this.translations = {
        nextStep: 'Next Step',
        skipStep: 'Skip Step',
        seeAllImages: 'See all images',
        addToCart: 'Add to Cart',
        validationError: 'Please select at least one scent to continue.',
        engravingSimulationNotice: 'The simulation is done for only 1 candle.',
        engravingCount: '{{ candleCount }} candles selected — {{ textCount }} texts entered',
        engravingBulkOrderNotice: 'For large orders (50–500 items), you can email the list of texts together with your order number instead of entering them one by one.',
        personalizationRequired: 'Choose a design, enter engraving text, or upload your logo to continue.',
        progressIndicator: 'Step {{ step }} of {{ total }}',
        notSelected: 'Not selected',
        notProvided: 'Not provided',
        engravingOptions: {
          catalogDesign: 'Catalog Design',
          textWithoutDesign: 'Text without design',
          myDesign: 'My Design'
        },
        itemCount: {
          one: '{{ count }} item',
          other: '{{ count }} items'
        },
        errors: {
          formNotFound: 'Unable to add to cart. Please try again.',
          invalidFileType: 'Please upload a valid image file (JPG, PNG, GIF, or WEBP)',
          fileTooLarge: 'File size must be less than 20MB'
        },
        upload: {
          customDesign: 'Custom Design',
          uploadDesign: 'Upload Design',
          modalTitle: 'Upload Your Custom Design',
          modalDescription: 'Choose an image file (JPG, PNG, or GIF) for your custom design',
          clickToUpload: 'Click to upload',
          dragAndDrop: 'or drag and drop',
          fileHint: 'JPG, PNG, GIF or WEBP (MAX. 20MB)',
          remove: 'Remove',
          cancel: 'Cancel',
          confirm: 'Confirm Upload',
          closeAriaLabel: 'Close upload',
          previewAlt: 'Custom design preview',
          pdfSelected: 'PDF file selected successfully',
          fileName: 'File name:'
        }
      };
    }
  }

  t(key, replacements = {}) {
    let translation = this.translations[key] || key;
    
    // Handle nested keys like 'itemCount.one'
    if (key.includes('.')) {
      const keys = key.split('.');
      translation = keys.reduce((obj, k) => obj?.[k], this.translations) || key;
    }

    // Decode HTML entities that Liquid's | t filter may introduce
    const textarea = document.createElement('textarea');
    textarea.innerHTML = translation;
    translation = textarea.value;
    
    // Replace placeholders like [step] or {{ step }} with actual values
    Object.keys(replacements).forEach(placeholder => {
      // Support both [placeholder] and {{ placeholder }} formats
      translation = translation.replace(
        new RegExp(`\\[\\s*${placeholder}\\s*\\]`, 'g'),
        replacements[placeholder]
      );
      translation = translation.replace(
        new RegExp(`{{\\s*${placeholder}\\s*}}`, 'g'),
        replacements[placeholder]
      );
    });
    
    return translation;
  }

  setupEventListeners() {
    const { triggerButton, closeButton, overlay, prevButton, nextButton } = this.refs;

    // Open/Close
    if (triggerButton && !Array.isArray(triggerButton)) {
      triggerButton.addEventListener('click', () => this.openPopup());
    }
    if (closeButton && !Array.isArray(closeButton)) {
      closeButton.addEventListener('click', () => this.closePopup());
    }
    if (overlay && !Array.isArray(overlay)) {
      overlay.addEventListener('click', () => this.closePopup());
    }

    // Navigation
    if (prevButton && !Array.isArray(prevButton)) {
      prevButton.addEventListener('click', () => this.previousStep());
    }
    if (nextButton && !Array.isArray(nextButton)) {
      nextButton.addEventListener('click', () => this.handleNextClick());
    }

    // Keyboard
    document.addEventListener('keydown', (e) => {
      if (this.dataset.open !== undefined) {
        if (e.key === 'Escape') this.closePopup();
        //if (e.key === 'ArrowLeft' && this.currentStep > 1) this.previousStep();
        //if (e.key === 'ArrowRight' && this.currentStep < 5) this.handleNextClick();
      }
    });

    // Step-specific listeners
    this.setupStepListeners();
  }

  setupStepListeners() {
    // Step 1: Scent selection (max 3)
    const scentCheckboxes = this.querySelectorAll('input[name^="properties[_scents_"]');
    scentCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleScentChange());
    });

    // Step 2: Engraving option selection
    const engravingOptionRadios = this.querySelectorAll('input[name="properties[_engraving_option]"]');
    engravingOptionRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleEngravingOptionChange());
    });

    // Step 3: Theme selection
    const themeRadios = this.querySelectorAll('input[name="properties[_theme]"]');
    themeRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleThemeChange());
    });

    // Step 4: Design selection
    const designRadios = this.querySelectorAll('input[name="properties[_design]"]');
    designRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.handleDesignChange(e));
    });

    const textWithoutDesignRadios = this.querySelectorAll('input[name="properties[_text_without_design]"]');
    textWithoutDesignRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.handleTextWithoutDesignChange(e));
    });

    // Step 4: Design zoom buttons
    const zoomButtons = this.querySelectorAll('.design-zoom-button, .upsell-product-card__image');
    zoomButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleZoomClick(e));
    });

    // Step 4: Custom design upload
    this.setupUploadListeners();

    // Step 5: Engraving input(s)
    this.setupEngravingInputListeners();

    // Step 7: Upsell button
    this.setupUpsellListeners();

    // Zoom modal close handlers
    const { zoomCloseButton, zoomOverlay } = this.refs;
    if (zoomCloseButton && !Array.isArray(zoomCloseButton)) {
      zoomCloseButton.addEventListener('click', () => this.closeZoomModal());
    }
    if (zoomOverlay && !Array.isArray(zoomOverlay)) {
      zoomOverlay.addEventListener('click', () => this.closeZoomModal());
    }
  }

  // Step 1: Scent handlers
  handleScentChange() {
    const checked = Array.from(this.querySelectorAll('input[name^="properties[_scents_"]:checked'));
    
    // Enforce max 3 scents
    if (checked.length > 3) {
      const oldest = checked[0];
      oldest.checked = false;
    }

    this.updateScentCounter();
    this.updateNextButton();
  }

  updateScentCounter() {
    const checked = this.querySelectorAll('input[name^="properties[_scents_"]:checked').length;
    const counter = this.querySelector('.selected-count');
    if (counter) {
      counter.textContent = checked;
    }
  }

  // Step 2: Engraving Option handlers
  handleEngravingOptionChange() {
    const selectedOption = this.querySelector('input[name="properties[_engraving_option]"]:checked');
    if (selectedOption) {
      this.selectedEngravingOption = {
        value: selectedOption.value,
        label: selectedOption.dataset.label || selectedOption.value
      };
      
      // Clear data from paths not taken
      this.clearUnusedPathData();

      if (selectedOption.value === 'my-design') {
        this.openUploadModal();
        this.updateNextButton();
        this.displayStep(this.currentStep);
        return;
      }
      
      // Auto-progress on first selection in the entry choice step
      if (!this.completedStepActions.has(this.currentStep)) {
        this.completedStepActions.add(this.currentStep);
        // Small delay to allow UI to update
        setTimeout(() => {
          this.handleNextClick();
        }, 50);
      }
    }
    this.updateNextButton();
    this.displayStep(this.currentStep);
  }

  clearUnusedPathData() {
    if (!this.selectedEngravingOption) return;

    // Clear all data except scents whenever engraving option changes
    // Reset theme to "view-all" as default
    this.selectedTheme = { 
      value: 'view-all', 
      label: this.t('seeAllImages')
    };
    this.selectedDesign = [];
    this.engravingText = '';
    this.engravingTexts = [];
    this.uploadedFile = null;
    
    // Clear UI selections
    this.clearThemeSelection();
    this.clearDesignSelection();
    this.clearEngravingInput();
    this.clearUploadPreview();
  }

  clearThemeSelection() {
    this.querySelectorAll('input[name="properties[_theme]"]').forEach(input => {
      input.checked = false;
    });
    // Re-select the "view-all" option as default
    const viewAllRadio = this.querySelector('input[name="properties[_theme]"][value="view-all"]');
    if (viewAllRadio) {
      viewAllRadio.checked = true;
    }
  }

  clearDesignSelection() {
    this.querySelectorAll('input[name="properties[_design]"]').forEach(input => {
      input.checked = false;
    });
    
    // Clear selection order when clearing designs
    this.designSelectionOrder = [];
    
    // Update badges to hide them
    if (this.enableMultipleDesigns) {
      this.updateSelectionOrderBadges();
    }
  }

  clearEngravingInput() {
    const rows = this.getEngravingRows();
    rows.forEach((row, index) => {
      if (index === 0) {
        this.resetEngravingRow(row);
      } else {
        row.remove();
      }
    });

    if (rows.length === 0) {
      this.addEngravingRow();
    }

    this.syncEngravingRowsState();
  }

  clearUploadPreview() {
    const { uploadPreview, uploadPlaceholder, uploadPreviewImage, uploadPreviewContainer, uploadPdfPlaceholder, uploadPdfFilename, uploadArea, uploadFileInput, uploadDesignRadio } = this.refs;
    
    // Clear the actual file input
    if (uploadFileInput && !Array.isArray(uploadFileInput)) {
      uploadFileInput.value = '';
    }
    
    // Uncheck the upload design radio button
    if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
      uploadDesignRadio.checked = false;
    }
    
    // Hide PDF success indicator in Step 8
    if (uploadPreview && !Array.isArray(uploadPreview)) {
      uploadPreview.classList.add('hidden');
    }
    
    if (uploadPlaceholder && !Array.isArray(uploadPlaceholder)) {
      uploadPlaceholder.style.display = '';
    }

    if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
      uploadPreviewImage.src = '';
      uploadPreviewImage.classList.add('hidden');
    }

    if (uploadPreviewContainer && !Array.isArray(uploadPreviewContainer)) {
      uploadPreviewContainer.classList.add('hidden');
    }
    
    if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
      uploadPdfPlaceholder.classList.add('hidden');
    }
    
    if (uploadPdfFilename && !Array.isArray(uploadPdfFilename)) {
      uploadPdfFilename.textContent = '';
    }

    if (uploadArea && !Array.isArray(uploadArea)) {
      uploadArea.style.display = '';
    }

    this.updatePersonalizationLockState();
  }

  getLiveEngravingTexts() {
    return this.getEngravingRows()
      .map(row => {
        return {
          quantity: this.normalizeEngravingQuantity(row.quantity),
          text: row.value.trim(),
          fontName: this.normalizeEngravingFontName(row.fontName),
        };
      })
      .filter(item => item.text !== '');
  }

  getEngravingRows() {
    return Array.from(this.querySelectorAll('[data-engraving-row]')).filter((row) => row instanceof HTMLElement);
  }

  getAssignedEngravingQuantity(entries = this.getLiveEngravingTexts()) {
    return entries.reduce((total, item) => total + item.quantity, 0);
  }

  normalizeEngravingQuantity(value) {
    const parsedValue = parseInt(value, 10);
    const fallbackValue = Number.isNaN(parsedValue) ? 1 : parsedValue;
    return Math.max(1, Math.min(this.selectedQuantity, fallbackValue));
  }

  normalizeEngravingFontName(fontName) {
    return ENGRAVING_FONT_NAMES.includes(fontName) ? fontName : 'mono';
  }

  getEngravingFontLabel(fontName) {
    return ENGRAVING_FONT_LABELS[this.normalizeEngravingFontName(fontName)] || ENGRAVING_FONT_LABELS.mono;
  }

  getEngravingPreviewTitle(quantity, fontName) {
    return this.t('embedded.engraving_preview', { count: quantity });
  }

  getSharedCandlePreviewConfig() {
    const candlePreview = this.refs.candlePreview;
    if (!candlePreview || Array.isArray(candlePreview)) {
      return {
        staticUrl: '',
        disclaimer: '',
        style: '',
        scale: '',
      };
    }

    return {
      staticUrl: candlePreview.dataset.staticUrl || '',
      disclaimer: candlePreview.dataset.disclaimer || '',
      style: candlePreview.getAttribute('style') || '',
      scale: candlePreview.style.getPropertyValue('--_scale'),
    };
  }

  getSelectedDesignImageUrl() {
    if (this.hasLogoUpload()) {
      return '';
    }

    const checkedDesign = this.querySelector('input[name="properties[_design]"]:checked');
    if (!checkedDesign) {
      return '';
    }

    const wrapper = checkedDesign.closest('.design-option');
    const img = wrapper?.querySelector('.design-image');
    return img?.src || '';
  }

  getEngravingRowTemplateAttributes() {
    const rowsContainer = this.querySelector('[data-engraving-rows]');
    if (!rowsContainer) {
      return {};
    }

    return {
      placeholder: rowsContainer.getAttribute('data-placeholder') || '',
      previewLabel: rowsContainer.getAttribute('data-preview-label') || 'Preview',
      removeLabel: rowsContainer.getAttribute('data-remove-label') || 'Remove engraving text',
      decreaseLabel: rowsContainer.getAttribute('data-decrease-label') || 'Decrease quantity',
      increaseLabel: rowsContainer.getAttribute('data-increase-label') || 'Increase quantity',
    };
  }

  ensureEngravingRowIds() {
    this.getEngravingRows().forEach((row) => {
      const currentValue = row.dataset.rowId || row.getAttribute('row-id');
      if (!currentValue) {
        this.engravingRowCounter += 1;
        row.dataset.rowId = `engraving-row-${this.engravingRowCounter}`;
      } else {
        const numericSuffix = parseInt(currentValue.split('-').pop(), 10);
        if (!Number.isNaN(numericSuffix)) {
          this.engravingRowCounter = Math.max(this.engravingRowCounter, numericSuffix);
        }
      }
    });
  }

  createEngravingRowElement(initialValues = {}) {
    const row = document.createElement('engraving-row');
    const labels = this.getEngravingRowTemplateAttributes();

    this.engravingRowCounter += 1;
    row.dataset.engravingRow = '';
    row.dataset.rowId = `engraving-row-${this.engravingRowCounter}`;
    row.setAttribute('placeholder', labels.placeholder);
    row.setAttribute('preview-label', labels.previewLabel);
    row.setAttribute('hide-font-selector', '');
    row.setAttribute('remove-label', labels.removeLabel);
    row.setAttribute('decrease-label', labels.decreaseLabel);
    row.setAttribute('increase-label', labels.increaseLabel);
    row.maxQuantity = this.selectedQuantity;
    row.quantity = initialValues.quantity || 1;
    row.fontName = initialValues.fontName || 'mono';
    row.value = initialValues.text || '';
    return row;
  }

  updateRemoveEngravingButtons() {
    const rows = this.getEngravingRows();
    const isLocked = this.hasLogoUpload();
    rows.forEach(row => {
      row.toggleAttribute('disabled', isLocked);
      row.canRemove = !isLocked && rows.length > 1;
    });
  }

  createEngravingWarningMessage(assignedQuantity) {
    const translatedMessage = this.t('engravingWarning', {
      textCount: assignedQuantity,
      currentQuantity: this.selectedQuantity,
      neededQuantity: assignedQuantity,
    });

    return translatedMessage === 'engravingWarning'
      ? this.t('embedded.engraving_allocation_warning', { assigned: assignedQuantity, quantity: this.selectedQuantity })
      : translatedMessage;
  }

  resetEngravingRow(row) {
    if (!(row instanceof HTMLElement)) return;

    row.value = '';
    row.quantity = 1;
    row.fontName = 'mono';
    row.maxQuantity = this.selectedQuantity;
    row.errorMessage = '';
  }

  addEngravingRow(initialValues = {}) {
    const rowsContainer = this.querySelector('[data-engraving-rows]');
    if (!rowsContainer) return null;

    const row = this.createEngravingRowElement(initialValues);
    row.toggleAttribute('disabled', this.hasLogoUpload());

    rowsContainer.appendChild(row);
    this.updateRemoveEngravingButtons();
    return row;
  }

  updateEngravingRowState(row) {
    if (!(row instanceof HTMLElement)) return;

    row.maxQuantity = this.selectedQuantity;
    row.quantity = this.normalizeEngravingQuantity(row.quantity);
    row.fontName = this.normalizeEngravingFontName(row.fontName);

    const hasEmoji = ENGRAVING_EMOJI_REGEX.test(row.value);
    row.errorMessage = hasEmoji && row.value.trim() !== ''
      ? this.t('errors.invalid_characters')
      : '';
    row.autoResize?.();
  }

  syncEngravingRowsState() {
    this.updateRemoveEngravingButtons();
    this.getEngravingRows().forEach(row => this.updateEngravingRowState(row));

    const liveTexts = this.getLiveEngravingTexts();
    const totalAssignedQuantity = this.getAssignedEngravingQuantity(liveTexts);
    const engravingCount = this.querySelector('[data-engraving-count]');
    const warningElement = this.querySelector('[data-engraving-total-warning]');
    const bulkOrderNotice = this.querySelector('[data-engraving-bulk-order-notice]');
    // const simulationNotice = this.querySelector('[data-engraving-simulation-notice]');

    // if (simulationNotice) {
    //   simulationNotice.textContent = this.t('engravingSimulationNotice');
    //   simulationNotice.style.display = liveTexts.length > 0 ? 'block' : 'none';
    // }

    if (engravingCount) {
      const textCountToken = '__ENGRAVING_TEXT_COUNT__';
      const countText = this.t('engravingCount', {
        candleCount: this.selectedQuantity,
        textCount: textCountToken,
      });
      const [textBeforeCount, textAfterCount = ''] = countText.split(textCountToken);
      const textCountElement = document.createElement('span');

      textCountElement.className = 'engraving-count__text-total';
      textCountElement.textContent = String(totalAssignedQuantity);
      textCountElement.classList.toggle(
        'engraving-count__text-total--over',
        totalAssignedQuantity > this.selectedQuantity,
      );

      engravingCount.replaceChildren(
        document.createTextNode(textBeforeCount),
        textCountElement,
        document.createTextNode(textAfterCount),
      );
    }

    if (bulkOrderNotice) {
      bulkOrderNotice.textContent = this.t('engravingBulkOrderNotice');
      bulkOrderNotice.style.display = this.selectedQuantity > 49 ? 'block' : 'none';
    }

    if (warningElement) {
      if (totalAssignedQuantity > this.selectedQuantity) {
        warningElement.textContent = this.createEngravingWarningMessage(totalAssignedQuantity);
        warningElement.style.display = 'block';
      } else {
        warningElement.textContent = '';
        warningElement.style.display = 'none';
      }
    }

    this.engravingTexts = liveTexts;
    this.engravingText = liveTexts.map(item => item.text).join(', ');
    // this.selectedEngravingOption = this.inferEngravingOption();
    this.updateNextButton();
    this.updateCandlePreviewVisibility();
    this.updateCandlePreview();
  }

  hasEngravingText() {
    return this.getLiveEngravingTexts().length > 0;
  }

  getSelectedDesignInputs() {
    return Array.from(this.querySelectorAll('input[name="properties[_design]"]:checked'));
  }

  hasCatalogDesign() {
    return this.getSelectedDesignInputs().length > 0;
  }

  hasLogoUpload() {
    return this.uploadedFile !== null;
  }

  hasTextWithoutDesignSelection() {
    return this.querySelector('input[name="properties[_text_without_design]"]:checked') !== null ||
      this.selectedEngravingOption?.value === 'text-without-design';
  }

  hasPersonalizationInput() {
    return this.hasLogoUpload() || this.hasCatalogDesign() || this.hasTextWithoutDesignSelection();
  }

  shouldShowPreviewStep() {
    return !this.hasLogoUpload() && (this.hasEngravingText() || this.hasCatalogDesign());
  }

  saveEngravingDataFromInputs() {
    const liveTexts = this.getLiveEngravingTexts();
    this.engravingTexts = liveTexts;
    this.engravingText = liveTexts.map(item => item.text).join(', ');
  }

  saveSelectedDesignDataFromInputs() {
    const designInputs = this.getSelectedDesignInputs();
    this.selectedDesign = designInputs.map(input => ({
      value: input.value,
      label: input.dataset.label || input.value
    }));
    this.updateSelectedThemeFromDesigns(designInputs);
  }

  updateSelectedThemeFromDesigns(designInputs = this.getSelectedDesignInputs()) {
    const themeLabels = [...new Set(designInputs
      .map(input => input.closest('.design-option')?.dataset.groupLabel)
      .filter(Boolean))];

    this.selectedTheme = themeLabels.length > 0
      ? {
          value: themeLabels.join(', ').toLowerCase().replace(/\s+/g, '-'),
          label: themeLabels.join(', ')
        }
      : { value: 'view-all', label: this.t('seeAllImages') };
  }

  inferEngravingOption() {
    if (this.hasLogoUpload()) {
      return {
        value: 'my-design',
        label: this.t('engravingOptions.myDesign')
      };
    }

    if (this.hasCatalogDesign()) {
      return {
        value: 'catalog-design',
        label: this.t('engravingOptions.catalogDesign')
      };
    }

    if (this.hasTextWithoutDesignSelection() || this.hasEngravingText()) {
      return {
        value: 'text-without-design',
        label: this.t('engravingOptions.textWithoutDesign')
      };
    }

    return null;
  }

  syncPersonalizationState() {
    if (this.hasLogoUpload()) {
      this.selectedDesign = [{
        value: 'custom-upload',
        label: this.t('upload.customDesign')
      }];
      this.engravingText = '';
      this.engravingTexts = [];
      this.selectedTheme = { value: 'view-all', label: this.t('seeAllImages') };
    } else {
      this.saveSelectedDesignDataFromInputs();
      this.saveEngravingDataFromInputs();
    }

    this.selectedEngravingOption = this.inferEngravingOption();
    this.updatePersonalizationLockState();
  }

  clearCatalogAndEngravingForLogo() {
    this.clearDesignSelection();
    this.clearEngravingInput();
    this.clearTextWithoutDesignSelection();
    this.selectedTheme = { value: 'view-all', label: this.t('seeAllImages') };
  }

  clearTextWithoutDesignSelection() {
    this.querySelectorAll('input[name="properties[_text_without_design]"]').forEach(input => {
      input.checked = false;
    });
  }

  updatePersonalizationLockState() {
    const isLocked = this.hasLogoUpload();
    const step = this.querySelector('[data-step="2"]');
    if (step) {
      step.toggleAttribute('data-logo-locked', isLocked);
    }

    this.getEngravingRows().forEach((row) => {
      row.toggleAttribute('disabled', isLocked);
    });

    this.querySelectorAll('[data-add-engraving-row]').forEach(button => {
      button.disabled = isLocked;
      button.setAttribute('aria-disabled', String(isLocked));
    });

    this.querySelectorAll('input[name="properties[_design]"]').forEach(input => {
      input.disabled = isLocked;
      input.setAttribute('aria-disabled', String(isLocked));
    });

    this.querySelectorAll('input[name="properties[_text_without_design]"]').forEach(input => {
      input.disabled = isLocked;
      input.setAttribute('aria-disabled', String(isLocked));
    });
  }

  handleTextWithoutDesignChange(e) {
    if (this.hasLogoUpload()) {
      if (e?.target) {
        e.target.checked = false;
      }
      this.updatePersonalizationLockState();
      return;
    }

    if (e?.target?.checked) {
      this.clearDesignSelection();
      this.selectedDesign = [];
      this.selectedTheme = { value: 'view-all', label: this.t('seeAllImages') };
    }

    this.selectedEngravingOption = this.inferEngravingOption();
    this.updateNextButton();
    this.updateCandlePreviewVisibility();
    this.updateCandlePreview();
  }

  // Step 3: Theme handlers
  handleThemeChange() {
    if (!this.completedStepActions.has(this.currentStep)) {
        this.completedStepActions.add(this.currentStep);
        // Small delay to allow UI to update
        setTimeout(() => {
          this.handleNextClick();
        }, 50);
      }

    this.updateNextButton();
  }

  // Step 3: Design handlers
  handleDesignChange(e) {
    if (this.hasLogoUpload()) {
      if (e?.target) {
        e.target.checked = false;
      }
      this.updatePersonalizationLockState();
      return;
    }

    if (this.enableMultipleDesigns) {
      const clickedInput = e?.target;
      
      // Update selection order tracking
      if (clickedInput) {
        const value = clickedInput.value;
        
        if (clickedInput.checked) {
          // Add to selection order if not already present
          if (!this.designSelectionOrder.includes(value)) {
            this.designSelectionOrder.push(value);
          }
        } else {
          // Remove from selection order when unchecked
          this.designSelectionOrder = this.designSelectionOrder.filter(v => v !== value);
        }
      }
      
      const checked = Array.from(this.querySelectorAll('input[name="properties[_design]"]:checked'));
      
      // Enforce max designs using actual selection order
      if (checked.length > this.maxDesigns) {
        // Find the oldest selection (first in order) and uncheck it
        const oldestValue = this.designSelectionOrder[0];
        const oldestInput = checked.find(input => input.value === oldestValue);
        
        if (oldestInput) {
          oldestInput.checked = false;
          // Remove from selection order
          this.designSelectionOrder.shift();
        }
      }
      
      // Update visual selection order badges
      this.updateSelectionOrderBadges();
    }
    this.clearTextWithoutDesignSelection();
    this.saveSelectedDesignDataFromInputs();
    this.selectedEngravingOption = this.inferEngravingOption();
    this.updateNextButton();
    this.updateCandlePreviewVisibility();
    this.updateCandlePreview();
  }

  updateSelectionOrderBadges() {
    // Get all design inputs
    const allDesignInputs = this.querySelectorAll('input[name="properties[_design]"]');
    
    // Get option colors from data attribute on this component
    let optionColors = [];
    if (this.dataset.optionColors) {
      try {
        optionColors = JSON.parse(this.dataset.optionColors);
      } catch (e) {
        console.warn('Failed to parse option colors:', e);
      }
    }
    
    allDesignInputs.forEach(input => {
      const wrapper = input.closest('.design-option');
      if (!wrapper) return;
      
      const badge = wrapper.querySelector('.selection-order-badge');
      if (!badge) return;
      
      // Find position in selection order
      const position = this.designSelectionOrder.indexOf(input.value);
      
      if (position >= 0 && input.checked) {
        // Show badge with number (1-indexed)
        badge.textContent = String(position + 1);
        badge.setAttribute('data-visible', 'true');
        badge.style.display = 'flex';
        
        // Apply color from option if available
        const color = optionColors[position] || '#000';
        badge.style.setProperty('background-color', color, 'important');
      } else {
        // Hide badge
        badge.setAttribute('data-visible', 'false');
        badge.style.display = 'none';
      }
    });
  }

  // Step 5: Engraving input listener setup
  setupEngravingInputListeners() {
    this.ensureEngravingRowIds();
    const rowsContainer = this.querySelector('[data-engraving-rows]');
    if (rowsContainer) {
      rowsContainer.addEventListener('engraving-row-change', (event) => {
        const row = event.target;
        this.updateEngravingRowState(row);
        this.syncEngravingRowsState();
      });

      rowsContainer.addEventListener('engraving-row-preview', (event) => {
        const detail = event.detail || {};
        this.openZoomModal(
          this.getSelectedDesignImageUrl(),
          this.getEngravingPreviewTitle(detail.quantity || 1, detail.fontName || 'mono'),
          false,
          true,
          'candle-preview',
          detail.text || '',
          detail.fontName || 'mono',
          true,
        );
      });

      rowsContainer.addEventListener('engraving-row-remove', (event) => {
        const row = event.target;
        const rows = this.getEngravingRows();
        if (!(row instanceof HTMLElement)) {
          return;
        }

        if (rows.length === 1) {
          this.resetEngravingRow(row);
        } else {
          row.remove();
        }

        this.syncEngravingRowsState();
      });
    }

    const addButton = this.querySelector('[data-add-engraving-row]');
    if (addButton) {
      addButton.addEventListener('click', () => {
        const row = this.addEngravingRow();
        this.syncEngravingRowsState();
        row?.focusTextInput?.();
      });
    }
  }

  // Navigation
  openPopup() {
    this.dataset.open = '';
    document.body.style.overflow = 'hidden';
    this.popupInstanceId = `psp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    this.hasTrackedCompletionForJourney = false;
    
    // Generate a unique ID for this personalization session
    this.logoLinkId = `logo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Generate a unique ID for upsell product linking
    this.upsellLinkId = `upsell_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Reset upsell state
    this.upsellSelected = false;
    this.resetUpsellCheckbox();
    
    // Reset completed step actions tracking for auto-progression
    this.completedStepActions.clear();
    
    // Get current quantity from product form
    this.updateQuantityFromForm();
    
    // Update max text count based on quantity
    this.updateMaxTextCount();
    
    // Initialize theme selection to "view-all" by default
    this.selectedTheme = {
      value: 'view-all',
      label: this.t('seeAllImages')
    };
    this.syncPersonalizationState();

    // Start each popup journey with a fresh entry choice. Keeping a radio
    // checked prevents a second click on that option from firing `change`.
    this.querySelectorAll('input[name="properties[_engraving_option]"]').forEach(input => {
      input.checked = false;
    });
    this.selectedEngravingOption = null;
    
    // Reset to the personalization type choice, or first enabled step
    this.currentStep = 10;
    if (!this.isStepEnabled(this.currentStep)) {
      const next = this.getNextStep(this.currentStep);
      if (next) {
        this.currentStep = next;
      } else {
        // Fallback: try to find ANY enabled step if the flow is broken
        const enabledStep = [10, 2, 3, 4, 5, 8, 9, 1, 7, 6].find(s => this.isStepEnabled(s));
        if (enabledStep) {
          this.currentStep = enabledStep;
        }
      }
    }

    this.displayStep(this.currentStep);
    this.updateUI();
  }

  closePopup() {
    delete this.dataset.open;
    document.body.style.overflow = '';
  }

  handleNextClick() {
    const stepConfig = this.getStepConfig(this.currentStep);

    if (this.currentStep === 5) {
      this.increaseQuantityForEngravings();
    }
    
    // Validate if required
    if (stepConfig.required && !this.validateCurrentStep()) {
      this.showValidationError();
      return;
    }

    // Save current step data
    this.saveCurrentStepData();

    // Get the next step based on current step and selections
    const nextStep = this.getNextStep();

    // If no next step (we're at review), add to cart
    if (nextStep === null) {
      this.handleAddToCart();
      return;
    }

    // Move to next step
    this.currentStep = nextStep;

    // Handle theme-based design filtering for step 4 (catalog design)
    if (this.currentStep === 4) {
      const allDesigns = this.querySelectorAll('.design-option--default');
      
      // Clear any previously selected designs when entering step 4
      // This ensures hidden selections don't block new selections
      this.selectedDesign = [];
      this.designSelectionOrder = [];
      
      // If "view-all" is selected or no theme selected, show all designs
      if (!this.selectedTheme || this.selectedTheme.value === 'view-all') {
        for (const design of allDesigns) {
          design.classList.remove('hidden');
        }
      } else {
        // Hide all designs first and uncheck hidden radio buttons
        for (const design of allDesigns) {
          design.classList.add('hidden');
          // Uncheck the radio/checkbox input when hiding
          const input = design.querySelector('input[name="properties[_design]"]');
          if (input) {
            input.checked = false;
          }
        }
        // Show only designs matching the selected theme
        let designs = this.querySelectorAll(`[data-group-label="${this.selectedTheme?.label}"]`);
        for (const design of designs) {
          design.classList.remove('hidden');
        }
      }
      
      // Update selection order badges when entering step 4
      if (this.enableMultipleDesigns) {
        this.updateSelectionOrderBadges();
      }
    }

    this.displayStep(this.currentStep);
    this.updateUI();
    this.scrollToTop();
  }

  getNextStep(fromStep = this.currentStep) {
    let nextStep = null;
    switch (fromStep) {
      case 10: // Personalization type choice
        switch (this.selectedEngravingOption?.value) {
          case 'catalog-design':
            nextStep = 2;
            break;
          case 'text-without-design':
            nextStep = 5;
            break;
          case 'my-design':
            nextStep = 1;
            break;
          default:
            nextStep = null;
        }
        break;

      case 2: // Universal personalization
        nextStep = this.hasLogoUpload() ? 1 : 5;
        break;
      
      case 3: // Theme -> Design (only for catalog-design path)
        nextStep = 4;
        break;
      
      case 4: // Design -> Engraving text
        nextStep = 5;
        break;
      
      case 5: // Engraving text -> Preview (Step 9)
        nextStep = this.shouldShowPreviewStep() ? 9 : 1;
        break;
      
      case 8: // Upload Design -> Scents (skip preview for own-design path)
        nextStep = 1;
        break;
      
      case 9: // Preview -> Scents (Step 1)
        nextStep = 1;
        break;
      
      case 1: // Scents -> Upsell (Step 7)
        nextStep = 7;
        break;
      
      case 7: // Upsell -> Review
        nextStep = 6;
        break;
      
      case 6: // Review -> Add to cart
        nextStep = null;
        break;
      
      default:
        nextStep = fromStep + 1;
    }

    // Recursively find the next enabled step
    if (nextStep !== null && !this.isStepEnabled(nextStep)) {
      return this.getNextStep(nextStep);
    }

    return nextStep;
  }

  previousStep() {
    const prevStep = this.getPreviousStep();
    if (prevStep !== null) {
      this.currentStep = prevStep;
      this.displayStep(this.currentStep);
      this.updateUI();
      this.scrollToTop();
    }
  }

  getPreviousStep(fromStep = this.currentStep) {
    let prevStep = null;
    switch (fromStep) {
      case 10: // Personalization type choice is the first step
        prevStep = null;
        break;

      case 2: // Universal personalization -> Personalization type choice
        prevStep = 10;
        break;
      
      case 3: // Theme -> Engraving Option (only for catalog-design)
        prevStep = 2;
        break;
      
      case 4: // Design -> Theme
        prevStep = 3;
        break;
      
      case 5: // Engraving text -> Dynamic based on engraving option
        prevStep = this.selectedEngravingOption?.value === 'text-without-design' ? 10 : 2;
        break;
      
      case 9: // Preview -> Universal personalization
        prevStep = 5;
        break;
      
      case 1: // Scents -> Preview when visible, otherwise universal personalization
        if (this.hasLogoUpload()) {
          prevStep = 10;
        } else {
          prevStep = this.shouldShowPreviewStep() ? 9 : 5;
        }
        break;
      
      case 7: // Upsell -> Scents (Step 1)
        prevStep = 1;
        break;
      
      case 6: // Review -> Upsell (Step 7)
        prevStep = 7;
        break;
      
      case 8: // Upload Design -> Engraving Option
        prevStep = 2;
        break;
      
      default:
        prevStep = fromStep - 1;
    }

    if (prevStep !== null && !this.isStepEnabled(prevStep)) {
      return this.getPreviousStep(prevStep);
    }

    return prevStep;
  }

  displayStep(stepNumber) {
    const steps = this.querySelectorAll('[data-step]');
    steps.forEach((step) => {
      const stepNum = parseInt(step.dataset.step);
      if (stepNum === stepNumber) {
        step.classList.add('personalized-steps-popup__step--active');
      } else {
        step.classList.remove('personalized-steps-popup__step--active');
      }
    });
    // Update max text count if the universal personalization/engraving step is visible.
    if (stepNumber === 2 || stepNumber === 5) {
      this.updateMaxTextCount();
      this.getEngravingRows().forEach((row) => row.autoResize?.());
    }
    this.updatePersonalizationLockState();

    this.updateCandlePreviewVisibility(stepNumber);
    this.updateCandlePreview();
    if (stepNumber === 6) {
      this.updateReviewSummary();
    }
    this.trackStepSeen(stepNumber);
  }

  updateCandlePreviewVisibility(stepNumber = this.currentStep) {
    const { candlePreviewContainer } = this.refs;
    if (candlePreviewContainer && !Array.isArray(candlePreviewContainer)) {
      if (this.shouldHideCandlePreview(stepNumber)) {
        candlePreviewContainer.classList.add('hidden');
      } else {
        candlePreviewContainer.classList.remove('hidden');
      }
    }
  }

  getFlowType() {
    switch (this.selectedEngravingOption?.value) {
      case 'catalog-design':
        return 'catalog_design';
      case 'text-without-design':
        return 'text_without_design';
      case 'my-design':
        return 'my_design';
      default:
        return 'unknown';
    }
  }

  getCurrentVariantId() {
    const productForm = this.closest('form[data-type="add-to-cart-form"]') ||
      this.closest('form[action*="/cart/add"]') ||
      document.querySelector('[data-type="add-to-cart-form"]') ||
      document.querySelector('form[action*="/cart/add"]');
    const variantInput = productForm?.querySelector('input[name="id"]') ||
      productForm?.querySelector('select[name="id"]') ||
      productForm?.closest('product-form-component')?.querySelector('input[name="id"]');
    const variantId = parseInt(variantInput?.value, 10);

    return Number.isNaN(variantId) ? null : variantId;
  }

  getSelectedDesignCount() {
    if (Array.isArray(this.selectedDesign) && this.selectedDesign.length > 0) {
      return this.selectedDesign.length;
    }

    return this.querySelectorAll('input[name="properties[_design]"]:checked').length;
  }

  getSelectedDesignNumbers() {
    if (!this.selectedDesign || (Array.isArray(this.selectedDesign) && this.selectedDesign.length === 0)) {
      return [];
    }

    const designs = Array.isArray(this.selectedDesign) ? this.selectedDesign : [this.selectedDesign];

    if (designs[0]?.value === 'custom-upload') {
      return [];
    }

    const pattern = /\b(?:[A-Za-z]+-(\d+)|(\d+)-[A-Za-z]+)\b/;

    return designs
      .map((design) => {
        const match = design.label?.match(pattern);
        return match?.[1] || match?.[2] || null;
      })
      .filter(Boolean);
  }

  buildAnalyticsPayload(stepNumber = null) {
    const selectedDesignNumbers = this.getSelectedDesignNumbers();
    const payload = {
      flow_type: this.getFlowType(),
      path_version: 'personalized_steps_v1',
      popup_instance_id: this.popupInstanceId,
      product_handle: this.dataset.productHandle || undefined,
      product_variant_id: this.getCurrentVariantId(),
      multiple_designs_enabled: this.enableMultipleDesigns,
      max_designs: this.maxDesigns,
      selected_quantity: this.selectedQuantity,
      selected_design_count: this.getSelectedDesignCount(),
      upsell_selected: this.upsellSelected,
      upsell_quantity: this.upsellSelected ? this.upsellQuantity : undefined,
    };

    if (selectedDesignNumbers.length > 0) {
      payload.selected_design_numbers = selectedDesignNumbers;
    }

    if (stepNumber !== null) {
      payload.step_number = stepNumber;
      payload.step_key = STEP_KEYS[stepNumber];
      payload.step_name = this.getStepTitle(stepNumber);
      payload.step_required = this.getStepConfig(stepNumber).required === true;
    }

    if (this.selectedTheme?.value && this.selectedTheme.value !== 'view-all') {
      payload.selected_theme = this.selectedTheme.value;
    }

    return payload;
  }

  capturePosthogEvent(eventName, payload) {
    if (!eventName || typeof window === 'undefined' || typeof window.posthog?.capture !== 'function') {
      return;
    }

    window.posthog.capture(eventName, payload);
  }

  trackStepSeen(stepNumber) {
    const eventName = STEP_EVENTS[stepNumber];
    const dedupeKey = `${this.dataset.blockId || 'personalized-steps'}:${stepNumber}`;

    if (!eventName || seenStepsThisTab.has(dedupeKey)) {
      return;
    }

    seenStepsThisTab.add(dedupeKey);
    
    this.capturePosthogEvent(eventName, this.buildAnalyticsPayload(stepNumber));
  }

  trackCompletedJourney() {
    if (this.hasTrackedCompletionForJourney) {
      return;
    }

    this.hasTrackedCompletionForJourney = true;
    this.capturePosthogEvent('personalized_steps_completed', this.buildAnalyticsPayload(6));
  }

  shouldHideCandlePreview(stepNumber) {
    // Custom uploaded designs do not have a reliable candle simulation.
    if (this.hasLogoUpload()) {
      return true;
    }

    if (stepNumber === 2) {
      return false;
    }

    if (stepNumber === 5) {
      return !this.hasEngravingText() && !this.hasCatalogDesign();
    }

    const hidePreviewSteps = [1, 3, 7]; // 1 = scents, 3 = theme, 7 = upsell
    return hidePreviewSteps.includes(stepNumber);
  }

  autoResizeTextarea(textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  updateUI() {
    this.updatePreviousButton();
    this.updateNextButton();
    this.updateProgress();
  }

  updatePreviousButton() {
    const { prevButton } = this.refs;
    if (prevButton && !Array.isArray(prevButton)) {
      const isDisabled = this.getPreviousStep() === null;
      prevButton.disabled = isDisabled;
      prevButton.setAttribute('aria-disabled', String(isDisabled));
    }
  }

  updateNextButton() {
    const { nextButton } = this.refs;
    if (!nextButton || Array.isArray(nextButton)) return;

    let buttonText;

    const nextStep = this.getNextStep();

    if (this.currentStep === 10 && !this.selectedEngravingOption) {
      buttonText = this.t('nextStep');
    } else if (nextStep === null) {
      buttonText = this.t('addToCart');
    } else {
      const nextStepTitle = this.getStepTitle(nextStep);
      buttonText = nextStepTitle;
    }

    const { nextButtonText } = this.refs;
    if (nextButtonText && !Array.isArray(nextButtonText)) {
      nextButtonText.textContent = buttonText;
    } else {
      nextButton.textContent = buttonText;
    }
  }

  getStepTitle(stepNumber) {
    const step = this.querySelector(`[data-step="${stepNumber}"]`);
    const stepName = step?.dataset.stepName?.trim();
    const title = step?.querySelector('.step-title')?.textContent?.trim();

    return stepName || title || this.t('nextStep');
  }

  updateProgress() {
    const stepCounter = this.refs.stepCounter;
    const progressIndicator = this.refs.progressIndicator;

    // Calculate dynamic path and current step index
    const path = this.calculateCurrentPath();
    const currentStepIndex = Math.max(path.indexOf(this.currentStep), 0) + 1;
    const totalSteps = path.length || 1;

    const counterText = `${currentStepIndex}/${totalSteps}`;
    const counterLabel = this.t('progressIndicator', {
      step: currentStepIndex,
      total: totalSteps
    });

    if (stepCounter && !Array.isArray(stepCounter)) {
      stepCounter.textContent = counterText;
      stepCounter.setAttribute('aria-label', counterLabel);
    }

    if (!progressIndicator || Array.isArray(progressIndicator)) return;

    const progressText = progressIndicator.querySelector('span');
    if (progressText) {
      progressText.textContent = this.t('progressIndicator', {
        step: currentStepIndex,
        total: totalSteps
      });
    }
  }

  calculateCurrentPath() {
    let fullPath;
    if (this.hasLogoUpload()) {
      fullPath = [10, 1, 7, 6];
    } else if (this.selectedEngravingOption?.value === 'text-without-design') {
      fullPath = this.shouldShowPreviewStep()
        ? [10, 5, 9, 1, 7, 6]
        : [10, 5, 1, 7, 6];
    } else if (this.selectedEngravingOption?.value === 'catalog-design') {
      fullPath = this.shouldShowPreviewStep()
        ? [10, 2, 5, 9, 1, 7, 6]
        : [10, 2, 5, 1, 7, 6];
    } else {
      fullPath = [10];
    }

    // Filter by enabled steps
    return fullPath.filter(step => this.isStepEnabled(step));
  }

  getStepConfig(stepNumber) {
    const config = {
      1: { required: true, skippable: false },
      2: { required: true, skippable: false },
      3: { required: false, skippable: true },
      4: { required: true, skippable: false },
      5: { required: false, skippable: true },
      6: { required: false, skippable: false },
      7: { required: false, skippable: true }, // Upsell step
      8: { required: false, skippable: true }, // Upload Design step
      9: { required: false, skippable: false }, // Candle Preview step
      10: { required: true, skippable: false }, // Personalization type choice
    };
    return config[stepNumber] || {};
  }

  currentStepHasInput() {
    switch (this.currentStep) {
      case 1: // Scents
        return this.querySelectorAll('input[name^="properties[_scents_"]:checked').length > 0;
      case 2: // Universal personalization
        return this.hasPersonalizationInput();
      case 3: // Theme
        return this.querySelector('input[name="properties[_theme]"]:checked') !== null;
      case 4: // Design
        return this.querySelector('input[name="properties[_design]"]:checked') !== null;
      case 5: // Engraving Text
        return this.hasEngravingText();
      case 6: // Review
        return true;
      case 7: // Upsell
        return this.upsellSelected;
      case 8: // Upload Design
        return this.uploadedFile !== null;
      case 9: // Candle Preview
        return true;
      case 10: // Personalization type choice
        return this.querySelector('input[name="properties[_engraving_option]"]:checked') !== null;
      default:
        return false;
    }
  }

  validateEngravingInputs() {
    if (this.hasLogoUpload()) {
      return true;
    }

    const rows = this.getEngravingRows();

    for (const row of rows) {
      if (!row.value.trim()) continue;

      if (ENGRAVING_EMOJI_REGEX.test(row.value)) {
        this.validationErrorMessage = this.t('errors.invalid_characters');
        return false;
      }
    }

    const liveTexts = this.getLiveEngravingTexts();
    const totalAssignedQuantity = this.getAssignedEngravingQuantity(liveTexts);
    if (totalAssignedQuantity > this.selectedQuantity) {
      this.validationErrorMessage = this.createEngravingWarningMessage(totalAssignedQuantity);
      return false;
    }

    return true;
  }

  validateCurrentStep() {
    if (this.currentStep === 10) {
      const selectedOption = this.querySelector('input[name="properties[_engraving_option]"]:checked');
      return selectedOption !== null && (selectedOption.value !== 'my-design' || this.uploadedFile !== null);
    }
    if (this.currentStep === 1) {
      // Must select at least 1 scent
      return this.querySelectorAll('input[name^="properties[_scents_"]:checked').length > 0;
    }
    if (this.currentStep === 2) {
      this.syncPersonalizationState();
      if (!this.hasPersonalizationInput()) {
        const personalizationRequiredMessage = this.t('personalizationRequired');
        this.validationErrorMessage = typeof personalizationRequiredMessage === 'string'
          && personalizationRequiredMessage.trim() !== ''
          && personalizationRequiredMessage !== 'personalizationRequired'
          ? personalizationRequiredMessage
          : 'Choose a design, enter engraving text, or upload your logo to continue.';
        return false;
      }

      return true;
    }
    if (this.currentStep === 4) {
      // Must select at least one design
      return this.querySelectorAll('input[name="properties[_design]"]:checked').length > 0;
    }
    if (this.currentStep === 5) {
      return this.validateEngravingInputs();
    }
    if (this.currentStep === 8) {
      // Must upload a design file
      return this.uploadedFile !== null;
    }
    return true;
  }

  showValidationError() {
    // Show error message
    const currentStepElement = this.querySelector(`[data-step="${this.currentStep}"]`);
    if (!currentStepElement) return;

    // Add shake animation or error styling
    currentStepElement.style.animation = 'shake 0.3s';
    setTimeout(() => {
      currentStepElement.style.animation = '';
    }, 300);

    // Show message
    const message = this.validationErrorMessage || this.t('validationError');
    alert(message);
    
    // Reset custom error message
    this.validationErrorMessage = null;
  }

  saveCurrentStepData() {
    console.log('Saving data for step', this.currentStep);
    switch (this.currentStep) {
      case 1: // Scents
        this.selectedScents = Array.from(this.querySelectorAll('input[name^="properties[_scents_"]:checked'))
          .map(input => ({
            value: input.value,
            label: input.dataset.label || input.value
          }));
        this.updateReviewSummary();
        break;
      case 2: // Universal personalization
        this.syncPersonalizationState();
        this.updateReviewSummary();
        break;
      case 3: // Theme
        const themeInput = this.querySelector('input[name="properties[_theme]"]:checked');
        this.selectedTheme = themeInput ? {
          value: themeInput.value,
          label: themeInput.dataset.label || themeInput.value
        } : null;
        break;
      case 4: // Design
        if (this.enableMultipleDesigns) {
          const designInputs = Array.from(this.querySelectorAll('input[name="properties[_design]"]:checked'));
          this.selectedDesign = designInputs.map(input => ({
            value: input.value,
            label: input.dataset.label || input.value
          }));
        } else {
          const designInput = this.querySelector('input[name="properties[_design]"]:checked');
          this.selectedDesign = designInput ? [{
            value: designInput.value,
            label: designInput.dataset.label || designInput.value
          }] : [];
        }

        console.log({saved: this.selectedDesign})
        break;
      case 5: // Engraving Text
        this.saveEngravingDataFromInputs();
        this.updateReviewSummary();
        break;
      case 6: // Review - update summary
        this.updateReviewSummary();
        break;
      case 7: // Upsell - save selection state, quantity and update review
        const { upsellCheckbox: upsellCb, upsellQuantityInput: upsellQtyInput } = this.refs;
        if (upsellCb && !Array.isArray(upsellCb)) {
          this.upsellSelected = upsellCb.checked;
        }
        if (upsellQtyInput && !Array.isArray(upsellQtyInput)) {
          this.upsellQuantity = parseInt(upsellQtyInput.value) || 1;
        }
        this.updateReviewSummary();
        break;
      case 8: // Upload Design - update review summary for my-design path
        this.updateReviewSummary();
        break;
      case 9: // Candle Preview - nothing to save, it's display only
        break;
    }
  }

  updateCandlePreview() {
    const candlePreview = this.refs.candlePreview;
    if (!candlePreview || Array.isArray(candlePreview)) return;

    const imageUrl = this.getSelectedDesignImageUrl();

    // Determine the engraving text – always take the first comma-separated segment
    let engravedText = '';
    let fontName = 'mono';

    if (this.currentStep === 2 || this.currentStep === 5) {
      const firstEntry = this.getLiveEngravingTexts()[0];
      engravedText = (firstEntry?.text || '').trim();
      fontName = this.normalizeEngravingFontName(firstEntry?.fontName || 'mono');
    } else if (this.engravingTexts && this.engravingTexts.length > 0) {
      engravedText = (this.engravingTexts[0]?.text || '').trim();
      fontName = this.normalizeEngravingFontName(this.engravingTexts[0]?.fontName || 'mono');
    } else if (this.engravingText) {
      engravedText = this.engravingText.split(',')[0].trim();
    }

    // Show placeholder text when a design is selected without live text.
    if (!engravedText && (this.currentStep === 2 || this.currentStep === 5) && imageUrl) {
      engravedText = this.t('previewPlaceholderText');
    }

    // Update the candle-preview attributes
    candlePreview.dataset.imageUrl = imageUrl;
    candlePreview.dataset.engravedText = engravedText;
    candlePreview.dataset.fontName = fontName;

    if (typeof candlePreview.refresh !== 'function' && candlePreview.shadowRoot) {
      candlePreview.disconnectedCallback?.();
      candlePreview.connectedCallback?.();
    }
  }

  updateReviewSummary() {
    // Quantity
    const quantityReview = this.querySelector('#review-quantity');
    if (quantityReview) {
      const itemKey = this.selectedQuantity === 1 ? 'itemCount.one' : 'itemCount.other';
      quantityReview.textContent = this.t(itemKey, { count: this.selectedQuantity });
    }

    // Scents
    const scentReview = this.querySelector('#review-scent');
    if (scentReview) {
      scentReview.textContent = this.selectedScents.length > 0
        ? this.selectedScents.map(s => s.label).join(', ')
        : this.t('notSelected');
    }

    // Engraving Option
    const engravingOptionReview = this.querySelector('#review-engraving-option');
    if (engravingOptionReview) {
      engravingOptionReview.textContent = this.selectedEngravingOption ? this.selectedEngravingOption.label : this.t('notSelected');
    }

    // Theme - only show if catalog-design path and not "view-all"
    const themeReviewItem = this.querySelector('.review-item--theme');
    const themeReview = this.querySelector('#review-theme');
    if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'catalog-design') {
      // Hide theme if "view-all" is selected (it's just a filter, not a real theme)
      if (this.selectedTheme && this.selectedTheme.value === 'view-all') {
        if (themeReviewItem) themeReviewItem.style.display = 'none';
      } else {
        if (themeReviewItem) themeReviewItem.style.display = '';
        if (themeReview) {
          themeReview.textContent = this.selectedTheme ? this.selectedTheme.label : this.t('notSelected');
        }
      }
    } else {
      if (themeReviewItem) themeReviewItem.style.display = 'none';
    }

    // Design - only show if catalog-design path
    const designReviewItem = this.querySelector('.review-item--design');
    const designReview = this.querySelector('#review-design');
    if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'catalog-design') {
      if (designReviewItem) designReviewItem.style.display = '';
      if (designReview) {
        if (Array.isArray(this.selectedDesign) && this.selectedDesign.length > 0) {
          designReview.textContent = this.selectedDesign.map(d => d.label).join(', ');
        } else {
          designReview.textContent = this.t('notSelected');
        }
      }
    } else if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'my-design') {
      // Show upload status for my-design path
      if (designReviewItem) designReviewItem.style.display = '';
      if (designReview) {
        designReview.textContent = this.uploadedFile ? this.t('upload.customDesign') : this.t('notSelected');
      }
    } else {
      if (designReviewItem) designReviewItem.style.display = 'none';
    }

    // Engraving - check if multi-input mode
    const engravingReviewMulti = this.querySelector('#review-engraving-multi');
    if (engravingReviewMulti && this.engravingTexts && this.engravingTexts.length > 0) {
      engravingReviewMulti.innerHTML = this.engravingTexts
        .map(item => `<p class="review-value"><strong>x${item.quantity}</strong> ${item.text}</p>`)
        .join('');
    } else if (engravingReviewMulti) {
      engravingReviewMulti.innerHTML = `<p class="review-value">${this.t('notProvided')}</p>`;
    }

    // Upsell - always show if step 7 is enabled, display selection status
    const upsellReviewItem = this.querySelector('.review-item--upsell');
    const upsellReview = this.querySelector('#review-upsell');
    if (upsellReviewItem && this.isStepEnabled(7)) {
      upsellReviewItem.style.display = '';
      if (upsellReview) {
        if (this.upsellSelected) {
          const qty = this.upsellQuantity || 1;
          upsellReview.textContent = qty > 1 
            ? `${this.t('upsellAdded') || 'Added'} (x${qty})`
            : this.t('upsellAdded') || 'Added';
        } else {
          upsellReview.textContent = this.t('upsellNotAdded') || 'Not added';
        }
      }
    } else if (upsellReviewItem) {
      upsellReviewItem.style.display = 'none';
    }
  }

  updateQuantityFromForm() {
    // Try to find quantity input in various forms
    const quantityInput = document.querySelector('input[name="quantity"]') ||
                         document.querySelector('[name="quantity"]') ||
                         document.querySelector('.quantity-selector input');
    
    if (quantityInput && quantityInput.value) {
      this.selectedQuantity = parseInt(quantityInput.value) || 1;
    } else {
      this.selectedQuantity = 1;
    }
    
    // Update max text count
    this.updateMaxTextCount();
    
    // Update the review immediately
    this.updateReviewSummary();
  }

  increaseQuantityForEngravings() {
    const assignedQuantity = this.getAssignedEngravingQuantity();
    if (assignedQuantity <= this.selectedQuantity) return;

    const productForm = this.closest('form[data-type="add-to-cart-form"]') ||
                        this.closest('form[action*="/cart/add"]') ||
                        document.querySelector('[data-type="add-to-cart-form"]') ||
                        document.querySelector('form[action*="/cart/add"]');
    const quantityInput = productForm?.querySelector('input[name="quantity"]') ||
                          document.querySelector('input[name="quantity"]');

    this.selectedQuantity = assignedQuantity;

    if (quantityInput) {
      quantityInput.value = String(assignedQuantity);
      quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(assignedQuantity));
    }

    this.updateMaxTextCount();
    this.updateReviewSummary();
  }

  updateMaxTextCount() {
    this.getEngravingRows().forEach(row => {
      row.maxQuantity = this.selectedQuantity;
      row.quantity = this.normalizeEngravingQuantity(row.quantity);
    });

    this.syncEngravingRowsState();
  }

  setLoadingState(loading) {
    const { nextButton } = this.refs;
    if (!nextButton || Array.isArray(nextButton)) return;

    if (loading) {
      nextButton.setAttribute('data-loading', '');
      nextButton.setAttribute('disabled', 'true');
    } else {
      nextButton.removeAttribute('data-loading');
      nextButton.removeAttribute('disabled');
    }
  }

  async handleAddToCart() {
    this.setLoadingState(true);

    // Find product form — the popup is rendered inside the form, so closest() is most reliable.
    // Fall back to document-level queries if the DOM structure differs.
    const productForm = this.closest('form[data-type="add-to-cart-form"]') ||
                        this.closest('form[action*="/cart/add"]') ||
                        document.querySelector('[data-type="add-to-cart-form"]') || 
                        document.querySelector('form[action*="/cart/add"]');
    
    if (!productForm) {
      console.error('Product form not found');
      this.setLoadingState(false);
      alert(this.t('errors.formNotFound'));
      return;
    }

    // Get main product variant ID and quantity from form
    const variantInput = productForm.querySelector('input[name="id"]') || 
                         productForm.querySelector('select[name="id"]') ||
                         productForm.closest('product-form-component')?.querySelector('input[name="id"]');
    const quantityInput = productForm.querySelector('input[name="quantity"]');
    
    if (!variantInput) {
      console.error('Variant input not found in form:', productForm);
      this.setLoadingState(false);
      alert(this.t('errors.formNotFound'));
      return;
    }

    const mainVariantId = parseInt(variantInput.value);
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : this.selectedQuantity;

    // Generate unique link ID for property-based linking
    const linkId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    
    // Store linkIds for injectPersonalizationData to use
    if (this.upsellSelected && this.upsellVariantId) {
      this.upsellLinkId = linkId;
    }
    if (this.selectedEngravingOption?.value === 'my-design' && this.customDesignVariantId) {
      this.logoLinkId = linkId;
    }

    // Inject personalization data into form (this sets all properties including link IDs)
    this.injectPersonalizationData(productForm);

    // Collect form data as properties for main product
    const formData = new FormData(productForm);
    const mainProperties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && value) {
        const propName = key.replace('properties[', '').replace(']', '');
        mainProperties[propName] = value;
      }
    }

    const cartAddUrl = window.Shopify?.routes?.root 
      ? `${window.Shopify.routes.root}cart/add.js` 
      : '/cart/add.js';

    try {
      // Helper to build child items with the correct parent reference
      const buildChildItems = (parentRef) => {
        const items = [];
        if (this.upsellSelected && this.upsellVariantId) {
          items.push({
            id: parseInt(this.upsellVariantId),
            quantity: this.upsellQuantity || 1,
            ...parentRef,
            properties: { '_upsell_link_id': linkId }
          });
        }
        if (this.selectedEngravingOption?.value === 'my-design' && this.customDesignVariantId) {
          items.push({
            id: parseInt(this.customDesignVariantId),
            quantity: 1,
            ...parentRef,
            properties: { '_logo_link_id': linkId }
          });
        }
        return items;
      };

      let mainResponse;
      let parsedResult;

      if (this.uploadedFile) {
        // === File upload path ===
        // Main product must be added alone via FormData (multipart/form-data)
        // so Shopify processes the file and generates a CDN link.
        const multipartData = new FormData();
        multipartData.append('id', String(mainVariantId));
        multipartData.append('quantity', String(quantity));

        for (const [key, value] of Object.entries(mainProperties)) {
          multipartData.append(`properties[${key}]`, value);
        }

        multipartData.append('properties[Image sur mesure]', this.uploadedFile);

        mainResponse = await fetch(cartAddUrl, {
          method: 'POST',
          body: multipartData,
        });

        if (!mainResponse.ok) {
          const errorData = await mainResponse.json();
          console.error('Error adding main product to cart:', errorData);
          this.setLoadingState(false);
          alert(errorData.description || this.t('errors.formNotFound'));
          return;
        }

        const mainResult = await mainResponse.json();
        console.log('Main product added to cart:', mainResult);

        // Parent already exists in cart → use parent_line_key (string = line item key)
        const childItems = buildChildItems({ parent_line_key: mainResult.key });

        if (childItems.length > 0) {
          const childResponse = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: childItems }),
          });

          if (!childResponse.ok) {
            console.error('Error adding child products to cart:', await childResponse.json());
          } else {
            console.log('Child products added to cart:', await childResponse.json());
          }
        }

        parsedResult = mainResult;
      } else {
        // === No file upload path ===
        // All items in a single request → use parent_id (integer = variant ID of parent in same request)
        const childItems = buildChildItems({ parent_id: mainVariantId });
        const allItems = [
          { id: mainVariantId, quantity: quantity, properties: mainProperties },
          ...childItems
        ];

        mainResponse = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: allItems }),
        });

        if (!mainResponse.ok) {
          const errorData = await mainResponse.json();
          console.error('Error adding products to cart:', errorData);
          this.setLoadingState(false);
          alert(errorData.description || this.t('errors.formNotFound'));
          return;
        }
      }

      const result = parsedResult || await mainResponse.json();
      console.log('Products added to cart:', result);
      this.trackCompletedJourney();

      // Fetch updated cart to get the full cart state with item_count
      const cartResponse = await fetch(window.Shopify?.routes?.root 
        ? `${window.Shopify.routes.root}cart.js` 
        : '/cart.js');
      const cart = await cartResponse.json();

      // Dispatch the theme's CartAddEvent to trigger cart drawer opening and refresh
      const { CartAddEvent } = await import('@theme/events');
      
      document.dispatchEvent(
        new CartAddEvent(
          cart,
          'personalized-steps-popup',
          {
            source: 'personalized-steps-popup',
            itemCount: cart.item_count,
          }
        )
      );

    } catch (error) {
      console.error('Error adding products to cart:', error);
      this.setLoadingState(false);
      alert(this.t('errors.formNotFound'));
      return;
    }

    this.setLoadingState(false);
    // Close popup
    this.closePopup();
  }

  injectPersonalizationData(form) {
    const visible_scent = form.querySelector("input[name='properties[Parfums]']")
    const visible_theme = form.querySelector("input[name='properties[Theme]']")
    const visible_design = form.querySelector("input[name='properties[Dessin]']")
    const visible_engraving = form.querySelector("input[name='properties[Gravure]']")
    const visible_gravure_option = form.querySelector("input[name='properties[Gravure Option]']")
    
    console.log({x: this.selectedDesign})
    visible_scent.value = this.selectedScents.map(s => s.label).join(', ');
    // Don't save "view-all" as it's just a filter, not a real theme
    visible_theme.value = (this.selectedTheme && this.selectedTheme.value !== 'view-all') ? this.selectedTheme.label : '';
    visible_design.value = (() => {
      if (!this.selectedDesign || (Array.isArray(this.selectedDesign) && this.selectedDesign.length === 0)) {
        return '';
      }
      
      // Normalize to array
      const designs = Array.isArray(this.selectedDesign) ? this.selectedDesign : [this.selectedDesign];
      
      // Handle custom upload case
      if (designs[0].value === 'custom-upload') {
        // Try to get the translated text, fallback to a user-friendly default
        const translatedText = this.t('upload.customDesign');
        return (translatedText && translatedText !== 'upload.customDesign') 
          ? translatedText 
          : 'Custom Design';
      }
      
      // For regular designs, extract the number or return the label
      const pattern = /\b(?:[A-Za-z]+-(\d+)|(\d+)-[A-Za-z]+)\b/;
      
      const values = designs.map(d => {
        const match = d.label?.match(pattern);
        if (match?.[1]) {
          return match[1];
        }
        if (match?.[2]) {
          return match[2];
        }
        return d.label;
      });
      
      return values.join(', ');
    })();
    visible_engraving.value = '';
    visible_gravure_option.value = this.selectedEngravingOption ? this.selectedEngravingOption.label : '';

    form.querySelectorAll('[data-generated-engraving-property="true"]').forEach(input => input.remove());

    if (this.engravingTexts && this.engravingTexts.length > 0) {
      this.engravingTexts.forEach((item, index) => {
        const appendGeneratedProperty = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          input.value = value;
          input.dataset.generatedEngravingProperty = 'true';
          form.appendChild(input);
        };

        appendGeneratedProperty(
          `Gravure ${index + 1}`,
          `${item.quantity}x ${item.text}`
        );
      });
    }

    // Add unique ID for "my-design" engraving option to link with custom design product
    if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'my-design') {
      // Check if _logo_link_id input already exists
      let logoLinkInput = form.querySelector("input[name='properties[_logo_link_id]']");
      
      if (!logoLinkInput) {
        // Create hidden input for _logo_link_id
        logoLinkInput = document.createElement('input');
        logoLinkInput.type = 'hidden';
        logoLinkInput.name = 'properties[_logo_link_id]';
        form.appendChild(logoLinkInput);
      }
      
      logoLinkInput.value = this.logoLinkId;
      
      // Also inject _engraving_option for cart handler to identify
      let engravingOptionInput = form.querySelector("input[data-generated-engraving-option='true']");
      
      if (!engravingOptionInput) {
        engravingOptionInput = document.createElement('input');
        engravingOptionInput.type = 'hidden';
        engravingOptionInput.name = 'properties[_engraving_option]';
        engravingOptionInput.dataset.generatedEngravingOption = 'true';
        form.appendChild(engravingOptionInput);
      }
      
      engravingOptionInput.value = 'my-design';
    } else {
      const logoLinkInput = form.querySelector("input[name='properties[_logo_link_id]']");
      if (logoLinkInput) {
        logoLinkInput.remove();
      }

      const engravingOptionInput = form.querySelector("input[data-generated-engraving-option='true']");
      if (engravingOptionInput) {
        engravingOptionInput.remove();
      }
    }

    // Add unique ID for upsell product linking
    if (this.upsellSelected && this.upsellLinkId) {
      let upsellLinkInput = form.querySelector("input[name='properties[_upsell_link_id]']");
      
      if (!upsellLinkInput) {
        upsellLinkInput = document.createElement('input');
        upsellLinkInput.type = 'hidden';
        upsellLinkInput.name = 'properties[_upsell_link_id]';
        form.appendChild(upsellLinkInput);
      }
      
      upsellLinkInput.value = this.upsellLinkId;

      // Add visible "Packaging" property when upsell is selected
      let packagingInput = form.querySelector("input[name='properties[Packaging]']");
      
      if (!packagingInput) {
        packagingInput = document.createElement('input');
        packagingInput.type = 'hidden';
        packagingInput.name = 'properties[Packaging]';
        form.appendChild(packagingInput);
      }
      
      packagingInput.value = this.t('embedded.added');
    }

  }

  submitForm(form) {
    const submitButton = form.querySelector('button[type="submit"]') || form.querySelector('[name="add"]');
    
    if (submitButton) {
      submitButton.click();
    } else {
      form.submit();
    }
  }

  async addUpsellProductToCart() {
    if (!this.upsellVariantId || !this.upsellLinkId) {
      console.warn('Upsell product not configured');
      return;
    }

    try {
      const formData = {
        items: [
          {
            id: this.upsellVariantId,
            quantity: 1,
            properties: {
              _upsell_link_id: this.upsellLinkId,
            },
          },
        ],
      };

      const response = await fetch(window.Shopify?.routes?.root 
        ? `${window.Shopify.routes.root}cart/add.js` 
        : '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error adding upsell product to cart:', errorData);
        return;
      }

      console.log('Upsell product added to cart with link ID:', this.upsellLinkId);
    } catch (error) {
      console.error('Error adding upsell product to cart:', error);
    }
  }

  clearAllSelections() {
    // Clear scents
    this.querySelectorAll('input[name^="properties[_scents_"]').forEach(input => {
      input.checked = false;
    });
    this.updateScentCounter();

    // Clear theme
    this.querySelectorAll('input[name="properties[_theme]"]').forEach(input => {
      input.checked = false;
    });

    // Clear design
    this.querySelectorAll('input[name="properties[_design]"]').forEach(input => {
      input.checked = false;
    });

    // Clear engraving
    this.clearEngravingInput();
    const charCount = this.querySelector('.char-count');
    if (charCount) {
      charCount.textContent = '0';
    }

    // Reset data
    // this.selectedScents = [];
    // this.selectedTheme = null;
    // this.selectedDesign = null;
    // this.engravingText = '';
  }

  scrollToTop() {
    const { stepContainer } = this.refs;
    if (stepContainer && !Array.isArray(stepContainer)) {
      stepContainer.scrollTop = 0;
    }
  }

  // Zoom Modal Methods
  handleZoomClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const button = e.currentTarget;
    const zoomTrigger = button.dataset.zoomImage ? button : button.closest('[data-zoom-image]');
    if (!zoomTrigger) return;

    const imageUrl = zoomTrigger.dataset.zoomImage;
    const imageTitle = zoomTrigger.dataset.zoomTitle;
    const showNote = zoomTrigger.dataset.zoomShowNote !== 'false';
    const showTitle = zoomTrigger.dataset.zoomShowTitle !== 'false';
    const mode = zoomTrigger.dataset.zoomMode || 'image';
    const defaultText = zoomTrigger.dataset.defaultText || '';
    const fontName = zoomTrigger.dataset.fontName || 'mono';
    
    this.openZoomModal(imageUrl, imageTitle, showNote, showTitle, mode, defaultText, fontName);
  }

  openZoomModal(imageUrl, imageTitle, showNote = true, showTitle = true, mode = 'image', defaultText = '', fontName = 'mono', useSharedPreviewConfig = false) {
    const { zoomModal, zoomImage, zoomTitle } = this.refs;
    
    if (!zoomModal || Array.isArray(zoomModal)) return;
    if (!zoomImage || Array.isArray(zoomImage)) return;
    if (!zoomTitle || Array.isArray(zoomTitle)) return;
    
    const zoomCandlePreview = this.refs.zoomCandlePreview;
    const useCandlePreview = mode === 'candle-preview' && zoomCandlePreview && !Array.isArray(zoomCandlePreview);
    let sharedPreviewScale = '';

    // Set image and title
    zoomImage.src = imageUrl;
    zoomImage.alt = imageTitle;
    zoomImage.style.display = useCandlePreview ? 'none' : '';

    if (useCandlePreview) {
      if (useSharedPreviewConfig) {
        const sharedPreviewConfig = this.getSharedCandlePreviewConfig();
        sharedPreviewScale = sharedPreviewConfig.scale;
        zoomCandlePreview.dataset.staticUrl = sharedPreviewConfig.staticUrl;
        zoomCandlePreview.dataset.disclaimer = sharedPreviewConfig.disclaimer;
        if (sharedPreviewConfig.style) {
          zoomCandlePreview.setAttribute('style', sharedPreviewConfig.style);
        } else {
          zoomCandlePreview.removeAttribute('style');
        }
      }

      zoomCandlePreview.dataset.imageUrl = imageUrl;
      zoomCandlePreview.dataset.engravedText = defaultText || zoomCandlePreview.dataset.defaultText || '';
      zoomCandlePreview.dataset.fontName = this.normalizeEngravingFontName(fontName);
      zoomCandlePreview.style.display = 'block';
      if (typeof zoomCandlePreview.refresh !== 'function' && zoomCandlePreview.shadowRoot) {
        zoomCandlePreview.disconnectedCallback?.();
        zoomCandlePreview.connectedCallback?.();
      }
    } else if (zoomCandlePreview && !Array.isArray(zoomCandlePreview)) {
      zoomCandlePreview.style.display = 'none';
    }

    zoomTitle.textContent = imageTitle;
    zoomTitle.style.display = showTitle ? '' : 'none';

    const zoomNote = zoomModal.querySelector('.design-zoom-modal__red-text');
    if (zoomNote) {
      zoomNote.style.display = showNote ? '' : 'none';
    }
    
    // On the first opening, the zoom preview's ResizeObserver runs after its
    // configuration is copied and replaces the shared scale with its own.
    // Let that initial observation finish while the modal is still hidden,
    // then reapply the universal preview scale before revealing the modal.
    if (useCandlePreview && useSharedPreviewConfig && sharedPreviewScale) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          zoomCandlePreview.style.setProperty('--_scale', sharedPreviewScale);
          zoomCandlePreview.refresh?.();
          zoomModal.dataset.open = '';
        });
      });
    } else {
      zoomModal.dataset.open = '';
    }
    
    // Add keyboard listener for escape
    document.addEventListener('keydown', this.handleZoomKeydown);
  }

  closeZoomModal() {
    const { zoomModal } = this.refs;
    if (!zoomModal || Array.isArray(zoomModal)) return;
    
    delete zoomModal.dataset.open;
    
    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleZoomKeydown);
  }

  handleZoomKeydown = (e) => {
    if (e.key === 'Escape') {
      this.closeZoomModal();
    }
  }

  // === Upload Functionality ===
  
  setupUploadListeners() {
    const { 
      uploadDesignRadio, 
      uploadFileInput, 
      uploadDropzone,
      uploadConfirmButton,
      uploadCancelButton,
      uploadCloseButton,
      uploadRemoveButton,
      uploadOverlay,
      uploadPreview
    } = this.refs;

    // When upload design option is clicked, open upload modal
    if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
      uploadDesignRadio.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.openUploadModal();
        }
      });

      uploadDesignRadio.addEventListener('click', () => {
        if (uploadDesignRadio.checked) {
          this.openUploadModal();
        }
      });

      uploadDesignRadio.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && uploadDesignRadio.checked) {
          event.preventDefault();
          this.openUploadModal();
        }
      });
    }

    // Allow clicking the success preview to reopen the modal for replacements
    const registerModalTrigger = (element) => {
      if (!element || Array.isArray(element)) return;
      element.addEventListener('click', () => {
        if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
          uploadDesignRadio.checked = true;
        }

        this.openUploadModal();
      });
    };

    registerModalTrigger(uploadPreview);

    // File input change
    if (uploadFileInput && !Array.isArray(uploadFileInput)) {
      uploadFileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    }

    // Drag and drop
    if (uploadDropzone && !Array.isArray(uploadDropzone)) {
      uploadDropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        uploadDropzone.classList.add('dragover');
      });

      uploadDropzone.addEventListener('dragleave', () => {
        uploadDropzone.classList.remove('dragover');
      });

      uploadDropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        uploadDropzone.classList.remove('dragover');
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          this.handleFile(files[0]);
        }
      });
    }

    // Confirm button
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.addEventListener('click', () => this.confirmUpload());
    }

    // Cancel button
    if (uploadCancelButton && !Array.isArray(uploadCancelButton)) {
      uploadCancelButton.addEventListener('click', () => this.cancelUpload());
    }

    // Close button
    if (uploadCloseButton && !Array.isArray(uploadCloseButton)) {
      uploadCloseButton.addEventListener('click', () => this.cancelUpload());
    }

    // Remove button
    if (uploadRemoveButton && !Array.isArray(uploadRemoveButton)) {
      uploadRemoveButton.addEventListener('click', () => this.removeUploadedFile());
    }

    // Overlay click
    if (uploadOverlay && !Array.isArray(uploadOverlay)) {
      uploadOverlay.addEventListener('click', () => this.cancelUpload());
    }
  }

  openUploadModal() {
    const { uploadModal } = this.refs;
    if (!uploadModal || Array.isArray(uploadModal)) return;

    if (uploadModal.dataset.open !== undefined) return;

    uploadModal.dataset.open = '';

    const { uploadConfirmButton } = this.refs;
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.disabled = !this.uploadedFile;
    }

    document.addEventListener('keydown', this.handleUploadKeydown);
  }

  closeUploadModal() {
    const { uploadModal } = this.refs;
    if (!uploadModal || Array.isArray(uploadModal)) return;
    
    delete uploadModal.dataset.open;
    document.removeEventListener('keydown', this.handleUploadKeydown);
  }

  handleUploadKeydown = (e) => {
    if (e.key === 'Escape') {
      this.cancelUpload();
    }
  }

  handleFileSelect(e) {
    const files = e.target.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  handleFile(file) {
    // Validate file type
    const validTypes = ['application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert(this.t('errors.invalidFileType'));
      return;
    }

    // Validate file size (20MB max)
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes
    if (file.size > maxSize) {
      alert(this.t('errors.fileTooLarge'));
      return;
    }

    // Store file temporarily
    this.uploadedFile = file;

    // Show preview
    this.showUploadPreview(file);

    // Enable confirm button
    const { uploadConfirmButton } = this.refs;
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.disabled = false;
    }
  }

  showUploadPreview(file) {
    const { uploadPreviewContainer, uploadPreviewImage, uploadPdfPlaceholder, uploadPdfFilename, uploadArea } = this.refs;
    
    if (!uploadPreviewContainer || Array.isArray(uploadPreviewContainer)) return;
    if (!uploadArea || Array.isArray(uploadArea)) return;

    // Show the preview container
    uploadPreviewContainer.classList.remove('hidden');
    uploadArea.style.display = 'none';

    // For PDF files, show placeholder instead of image preview
    if (file.type === 'application/pdf') {
      if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
        uploadPdfPlaceholder.classList.remove('hidden');
      }
      
      if (uploadPdfFilename && !Array.isArray(uploadPdfFilename)) {
        uploadPdfFilename.textContent = `${this.t('upload.fileName')} ${file.name}`;
      }
      
      // Hide image preview
      if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
        uploadPreviewImage.classList.add('hidden');
      }
    } else {
      // For image files, show image preview (fallback, though we only accept PDFs now)
      if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          uploadPreviewImage.src = e.target.result;
          uploadPreviewImage.classList.remove('hidden');
        };
        reader.readAsDataURL(file);
      }
      
      // Hide PDF placeholder
      if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
        uploadPdfPlaceholder.classList.add('hidden');
      }
    }
  }

  removeUploadedFile() {
    const { 
      uploadPreviewContainer, 
      uploadPreviewImage,
      uploadPdfPlaceholder,
      uploadPdfFilename,
      uploadArea, 
      uploadFileInput, 
      uploadConfirmButton 
    } = this.refs;

    // Clear file
    this.uploadedFile = null;
    this.selectedDesign = [];
    this.selectedEngravingOption = this.inferEngravingOption();
    
    if (uploadFileInput && !Array.isArray(uploadFileInput)) {
      uploadFileInput.value = '';
    }

    // Hide preview container
    if (uploadPreviewContainer && !Array.isArray(uploadPreviewContainer)) {
      uploadPreviewContainer.classList.add('hidden');
    }
    
    // Clear image preview
    if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
      uploadPreviewImage.src = '';
      uploadPreviewImage.classList.add('hidden');
    }
    
    // Clear PDF placeholder
    if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
      uploadPdfPlaceholder.classList.add('hidden');
    }
    
    if (uploadPdfFilename && !Array.isArray(uploadPdfFilename)) {
      uploadPdfFilename.textContent = '';
    }
    
    // Show upload area
    if (uploadArea && !Array.isArray(uploadArea)) {
      uploadArea.style.display = '';
    }

    // Disable confirm button
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.disabled = true;
    }

    const { uploadPreview, uploadPlaceholder, uploadDesignRadio } = this.refs;
    if (uploadPreview && !Array.isArray(uploadPreview)) {
      uploadPreview.classList.add('hidden');
    }
    if (uploadPlaceholder && !Array.isArray(uploadPlaceholder)) {
      uploadPlaceholder.style.display = '';
    }
    if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
      uploadDesignRadio.checked = false;
    }

    this.syncPersonalizationState();
    this.updateNextButton();
    this.updateCandlePreviewVisibility();
    this.updateCandlePreview();
  }

  confirmUpload() {
    if (!this.uploadedFile) return;

    const { uploadPreview, uploadPlaceholder } = this.refs;

    this.clearCatalogAndEngravingForLogo();

    // Show PDF success indicator in the design option (Step 8)
    if (uploadPreview && !Array.isArray(uploadPreview)) {
      uploadPreview.classList.remove('hidden');
    }

    // Hide placeholder
    if (uploadPlaceholder && !Array.isArray(uploadPlaceholder)) {
      uploadPlaceholder.style.display = 'none';
    }

    // Update selected design
    this.selectedDesign = [{
      value: 'custom-upload',
      label: this.t('upload.customDesign')
    }];
    this.selectedEngravingOption = this.inferEngravingOption();
    this.updatePersonalizationLockState();
    this.updateNextButton();
    this.updateCandlePreviewVisibility();
    this.updateCandlePreview();

    // Close modal
    this.closeUploadModal();
    
    // Keep legacy auto-progress only if the old upload-only step is active.
    if ((this.currentStep === 8 || this.currentStep === 10) && !this.completedStepActions.has(this.currentStep)) {
      this.completedStepActions.add(this.currentStep);
      // Small delay to allow UI to update
      setTimeout(() => {
        this.handleNextClick();
      }, 50);
    }
  }

  cancelUpload() {
    const { uploadDesignRadio } = this.refs;

    // If no file was confirmed, uncheck the upload radio
    const isCustomUpload = Array.isArray(this.selectedDesign) && 
                           this.selectedDesign.length > 0 && 
                           this.selectedDesign[0].value === 'custom-upload';

    if (!this.uploadedFile || !isCustomUpload) {
      if (this.uploadedFile && !isCustomUpload) {
        this.removeUploadedFile();
      }
      if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
        uploadDesignRadio.checked = false;
      }

      if (this.currentStep === 10) {
        const customImageOption = this.querySelector('input[name="properties[_engraving_option]"][value="my-design"]');
        if (customImageOption) {
          customImageOption.checked = false;
        }
        this.selectedEngravingOption = null;
        this.updateNextButton();
      }
    }

    // Close modal
    this.closeUploadModal();
  }

  // === Upsell Functionality ===
  
  setupUpsellListeners() {
    const { upsellCheckbox, upsellQuantityInput, upsellQuantityMinus, upsellQuantityPlus } = this.refs;
    
    if (upsellCheckbox && !Array.isArray(upsellCheckbox)) {
      upsellCheckbox.addEventListener('change', () => this.handleUpsellChange());
    }

    if (upsellQuantityInput && !Array.isArray(upsellQuantityInput)) {
      upsellQuantityInput.addEventListener('input', () => this.handleUpsellQuantityChange());
      upsellQuantityInput.addEventListener('change', () => this.handleUpsellQuantityChange());
    }

    if (upsellQuantityMinus && !Array.isArray(upsellQuantityMinus)) {
      upsellQuantityMinus.addEventListener('click', () => {
        const { upsellQuantityInput } = this.refs;
        if (!upsellQuantityInput || Array.isArray(upsellQuantityInput)) return;
        const current = parseInt(upsellQuantityInput.value) || 1;
        if (current > 1) {
          upsellQuantityInput.value = current - 1;
          this.handleUpsellQuantityChange();
        }
      });
    }

    if (upsellQuantityPlus && !Array.isArray(upsellQuantityPlus)) {
      upsellQuantityPlus.addEventListener('click', () => {
        const { upsellQuantityInput } = this.refs;
        if (!upsellQuantityInput || Array.isArray(upsellQuantityInput)) return;
        const current = parseInt(upsellQuantityInput.value) || 1;
        upsellQuantityInput.value = current + 1;
        this.handleUpsellQuantityChange();
      });
    }
  }

  handleUpsellChange() {
    const { upsellCheckbox, upsellQuantityWrapper, upsellQuantityInput } = this.refs;
    if (!upsellCheckbox || Array.isArray(upsellCheckbox)) return;
    
    // Update selection state based on checkbox
    this.upsellSelected = upsellCheckbox.checked;

    // Show/hide quantity input
    if (upsellQuantityWrapper && !Array.isArray(upsellQuantityWrapper)) {
      if (this.upsellSelected) {
        upsellQuantityWrapper.classList.remove('hidden');
      } else {
        upsellQuantityWrapper.classList.add('hidden');
        // Reset quantity to 1 when unchecked
        if (upsellQuantityInput && !Array.isArray(upsellQuantityInput)) {
          upsellQuantityInput.value = 1;
        }
        this.upsellQuantity = 1;
        this.hideUpsellQuantityWarning();
      }
    }
    
    this.updateNextButton();
  }

  handleUpsellQuantityChange() {
    const { upsellQuantityInput } = this.refs;
    if (!upsellQuantityInput || Array.isArray(upsellQuantityInput)) return;

    let value = parseInt(upsellQuantityInput.value);
    if (isNaN(value) || value < 1) {
      value = 1;
      upsellQuantityInput.value = 1;
    }
    this.upsellQuantity = value;

    // Check against ceil(product quantity / 2) and show/hide warning
    const maxUpsellQuantity = Math.ceil(this.selectedQuantity / 2);
    if (value > maxUpsellQuantity) {
      this.showUpsellQuantityWarning();
    } else {
      this.hideUpsellQuantityWarning();
    }
  }

  showUpsellQuantityWarning() {
    const { upsellQuantityWarning } = this.refs;
    if (!upsellQuantityWarning || Array.isArray(upsellQuantityWarning)) return;

    const maxUpsellQuantity = Math.ceil(this.selectedQuantity / 2);
    upsellQuantityWarning.textContent = this.t('upsellQuantityWarning', {
      upsellQuantity: this.upsellQuantity,
      packagingPlaces: this.upsellQuantity * 2,
      productQuantity: this.selectedQuantity
    });
    upsellQuantityWarning.classList.remove('hidden');
  }

  hideUpsellQuantityWarning() {
    const { upsellQuantityWarning } = this.refs;
    if (!upsellQuantityWarning || Array.isArray(upsellQuantityWarning)) return;
    
    upsellQuantityWarning.classList.add('hidden');
  }

  resetUpsellCheckbox() {
    const { upsellCheckbox, upsellQuantityWrapper, upsellQuantityInput } = this.refs;
    if (!upsellCheckbox || Array.isArray(upsellCheckbox)) return;
    
    upsellCheckbox.checked = false;
    this.upsellSelected = false;
    this.upsellQuantity = 1;

    if (upsellQuantityInput && !Array.isArray(upsellQuantityInput)) {
      upsellQuantityInput.value = 1;
    }
    if (upsellQuantityWrapper && !Array.isArray(upsellQuantityWrapper)) {
      upsellQuantityWrapper.classList.add('hidden');
    }
    this.hideUpsellQuantityWarning();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleZoomKeydown);
    document.removeEventListener('keydown', this.handleUploadKeydown);
  }
}

// Register custom element
customElements.define('personalized-steps-popup-component', PersonalizedStepsPopupComponent);
